/**
 * Utility functions for development and testing
 * This file provides helpers for local development and testing
 */
import { logger } from './logger';
import { shouldUseEmulators } from './environmentConfig';

/**
 * Checks if the application should use development features
 * @returns boolean indicating if development features should be used
 */
export const shouldUseDevelopmentFeatures = (): boolean => {
  // Development features should only be used with emulators
  return shouldUseEmulators();
};

/**
 * Checks if forms should be prefilled with demo data
 * @returns boolean indicating if forms should be prefilled
 */
export const shouldPrefillForms = (): boolean => {
  if (!shouldUseDevelopmentFeatures()) {
    return false;
  }
  return process.env.REACT_APP_PREFILL_FORMS === 'true';
};

/**
 * Loads a demo image file from the public directory
 * @param imagePath Relative path to the image in the public directory
 * @param fileName Name to use for the created File object
 * @returns Promise resolving to an object with the file and preview URL
 */
export const loadDemoImage = async (
  imagePath: string = '/images/background.jpg',
  fileName: string = 'demo-image.jpg'
): Promise<{ file: File | null; previewUrl: string | null }> => {
  if (!shouldUseDevelopmentFeatures()) {
    return { file: null, previewUrl: null };
  }

  const demoImagePath = process.env.PUBLIC_URL + imagePath;
  logger.info('🔄 Loading demo image from:', demoImagePath);

  try {
    const res = await fetch(demoImagePath);
    if (!res.ok) {
      throw new Error(`Failed to fetch demo image: ${res.status} ${res.statusText}`);
    }
    
    const blob = await res.blob();
    const demoFile = new File([blob], fileName, { type: 'image/jpeg' });
    const previewUrl = URL.createObjectURL(demoFile);
    
    logger.success('📸 Demo image loaded successfully');
    return { file: demoFile, previewUrl };
  } catch (err) {
    logger.error('❌ Failed to load demo image:', err);
    logger.info(`💡 Make sure the image exists at public${imagePath}`);
    return { file: null, previewUrl: null };
  }
};

/**
 * Returns prefill data for the event creation form in development mode
 * @returns An object with prefilled event data
 */
export const getEventFormPrefillData = () => {
  if (!shouldUseDevelopmentFeatures()) {
    return null;
  }

  // Format today's date as YYYY-MM-DD for the date input
  const today = new Date();
  const nextMonth = new Date(today);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  
  const formattedDate = nextMonth.toISOString().split('T')[0];
  
  logger.info('🧪 Prefilling event form with test data (development only)');
  
  return {
    title: 'Test Event ' + Math.floor(Math.random() * 1000),
    date: formattedDate,
    imageUrl: '',
    address: '123 Test Street, Test City',
    description: 'This is a test event created for development purposes. This description is automatically generated to help with testing the event creation functionality.',
    startTime: { 
      doorsOpen: '19:00', 
      doorsClose: '23:00' 
    },
    endTime: '23:30',
  };
};

/**
 * Returns prefill data for the application form in development mode
 * @returns An object with prefilled application data
 */
export const getApplicationFormPrefillData = () => {
  if (!shouldPrefillForms()) {
    return null;
  }
  
  logger.info('🧪 Prefilling application form with test data (development only)');
  
  return {
    name: 'John Doe',
    email: process.env.REACT_APP_PREFILL_EMAIL || 'john.doe@example.com',
    understandingConsent: 'I understand that consent is an explicit, ongoing agreement between all parties involved. It can be revoked at any time, and clear communication is essential. I will always respect boundaries and ensure all interactions are consensual.',
    attendeeType: 'couple' as const,
    partnerAlias: 'Jane Doe',
    consentAcknowledgment: true,
    dataRetentionAgreement: true
  };
};
